// Angular
import { BrowserModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { LOCALE_ID, APP_INITIALIZER, NgModule } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { GestureConfig, MatProgressSpinnerModule } from '@angular/material';
import { OverlayModule } from '@angular/cdk/overlay';
// Angular in memory
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
// Perfect Scroll bar
import { PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
// SVG inline
import { InlineSVGModule } from 'ng-inline-svg';
// Env
import { environment } from '../environments/environment';
// Hammer JS
import 'hammerjs';
// NGX Permissions
import { NgxPermissionsModule } from 'ngx-permissions';
// NGRX
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
// State
import { metaReducers, reducers } from './core/reducers';
// Copmponents
import { AppComponent } from './app.component';
// Modules
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { ThemeModule } from './views/theme/theme.module';
// Partials
import { PartialsModule } from './views/partials/partials.module';
// Layout Services

import { BasicDialogService } from './core/_base/services/basic-dialog.service';
import { DeleteDialogService } from './core/_base/services/delete-dialog.service';

import {
  DataTableService,
  FakeApiService,
  KtDialogService,
  LayoutConfigService,
  LayoutRefService,
  MenuAsideService,
  MenuConfigService,
  MenuHorizontalService,
  PageConfigService,
  SplashScreenService,
  SubheaderService
} from './core/_base/layout';
// Auth
import { AuthModule } from './views/pages/auth/auth.module';
import { AuthService } from './core/auth';
// CRUD
import { HttpUtilsService, LayoutUtilsService, TypesUtilsService } from './core/_base/crud';
// Config
import { LayoutConfig } from './core/_config/layout.config';
// Highlight JS
import { HIGHLIGHT_OPTIONS, HighlightLanguage } from 'ngx-highlightjs';
import * as typescript from 'highlight.js/lib/languages/typescript';
import * as scss from 'highlight.js/lib/languages/scss';
import * as xml from 'highlight.js/lib/languages/xml';
import * as json from 'highlight.js/lib/languages/json';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { GrainTypeService, SiloService, PavilionService } from './core/plant-configuration';
import { BirdService } from './core/plant-configuration/_services/bird.service';

import localeESCL from '@angular/common/locales/es-CL';
import { registerLocaleData } from '@angular/common';
import { CompanyService } from './core/auth/_services/company.service';
import { CommonFieldsInterceptor } from './core/_base/interceptors/common-fields.interceptor';
import { TokenInterceptor } from './core/_base/interceptors/token.interceptor';
import { PlantService } from './core/plant-configuration/_services/plant.service';
import { ModuleService } from './core/plant-configuration/_services/module.service';
import { GroupService } from './core/auth/_services/group.service';
import { UnitService } from './core/production/_services/units.service';
// import { ApplyUnitPipe } from './core/production/_pipes/apply-unit.pipe';
import { ThemeDirective } from './core/_base/layout/directives/theme.directive';
import { ThemeService } from './core/auth/_services/theme.service';
import { PartnerService } from './core/auth/_services/partner.service';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { ProductV2Service } from './core/plant-configuration/_services/productv2.service';
import { WorkOrderService } from './core/plant-configuration/_services/work-order.service';
import { LotService } from './core/plant-configuration/_services/lot.service';
import { ProductSessionService } from './core/production/_services/product-session.service';
import { ShiftSessionService } from './core/production/_services/shit-session.service';
import { DetentionV2Service } from './core/production/_services/detentionv2.service';
registerLocaleData(localeESCL);

// tslint:disable-next-line:class-name
const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
	wheelSpeed: 0.5,
	swipeEasing: true,
	minScrollbarLength: 40,
	maxScrollbarLength: 300,
};

export function initializeLayoutConfig(appConfig: LayoutConfigService) {
	// initialize app by loading default demo layout config
	return () => {
		if (appConfig.getConfig() === null) {
			appConfig.loadConfigs(new LayoutConfig().configs);
		}
	};
}

export function hljsLanguages(): HighlightLanguage[] {
	return [
		{name: 'typescript', func: typescript},
		{name: 'scss', func: scss},
		{name: 'xml', func: xml},
		{name: 'json', func: json}
	];
}

@NgModule({
	declarations: [AppComponent, ThemeDirective],
	imports: [
		BrowserAnimationsModule,
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		environment.isMockEnabled ? HttpClientInMemoryWebApiModule.forRoot(FakeApiService, {
			passThruUnknownUrl: true,
			dataEncapsulation: false
		}) : [],
		NgxPermissionsModule.forRoot(),
		PartialsModule,
		CoreModule,
		OverlayModule,
		StoreModule.forRoot(reducers, {metaReducers}),
		EffectsModule.forRoot([]),
		StoreRouterConnectingModule.forRoot({stateKey: 'router'}),
		StoreDevtoolsModule.instrument(),
		AuthModule.forRoot(),
		TranslateModule.forRoot(),
		MatProgressSpinnerModule,
		InlineSVGModule.forRoot(),
		ThemeModule,
		CalendarModule.forRoot({ provide: DateAdapter, useFactory: adapterFactory }),
		SweetAlert2Module.forRoot()
	],
	exports: [],
	providers: [
		{ provide: HTTP_INTERCEPTORS, useClass: CommonFieldsInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
		AuthService,
		PavilionService,
		SiloService,
		GrainTypeService,
		BirdService,
		LayoutConfigService,
		LayoutRefService,
		MenuConfigService,
		PageConfigService,
		KtDialogService,
		DataTableService,
		SplashScreenService,
		PlantService,
		ProductV2Service,
		WorkOrderService,
		LotService,
		ModuleService,
		ThemeService,
		{
			provide: PERFECT_SCROLLBAR_CONFIG,
			useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
		},
		{
			provide: HAMMER_GESTURE_CONFIG,
			useClass: GestureConfig
		},
		{
			// layout config initializer
			provide: APP_INITIALIZER,
			useFactory: initializeLayoutConfig,
			deps: [LayoutConfigService], multi: true
		},
		{
			provide: HIGHLIGHT_OPTIONS,
			useValue: {languages: hljsLanguages}
		},
		{ provide: LOCALE_ID, useValue: 'es-CL' },
		// template services
		SubheaderService,
		MenuHorizontalService,
		MenuAsideService,
		HttpUtilsService,
		TypesUtilsService,
		LayoutUtilsService,
		CompanyService,
		GroupService,
		UnitService,
		PartnerService,
		BasicDialogService,
		DeleteDialogService,
		ProductSessionService,
		ShiftSessionService,
		DetentionV2Service
	],
	bootstrap: [AppComponent]
})
export class AppModule {
}
